import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '../../components/Logo/Logo';
import { apiService } from '../../services/api';
import './Register.css';

interface RegisterForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  organizationName: string;
  organizationSlug: string;
  planId: string;
}

const plans = [
  { id: 'free', name: 'Free', price: 0, users: 1 },
  { id: 'starter', name: 'Starter', price: 49, users: 3 },
  { id: 'business', name: 'Business', price: 199, users: 10 },
  { id: 'enterprise', name: 'Enterprise', price: -1, users: 'Personalizados' } // -1 = Sob consulta
];

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [form, setForm] = useState<RegisterForm>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organizationName: '',
    organizationSlug: '',
    planId: searchParams.get('plan') || 'free'
  });

  useEffect(() => {
    const planFromUrl = searchParams.get('plan');
    if (planFromUrl && plans.find(p => p.id === planFromUrl)) {
      setForm(prev => ({ ...prev, planId: planFromUrl }));
    }
  }, [searchParams]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'organizationName') {
        updated.organizationSlug = generateSlug(value);
      }
      return updated;
    });
    setError('');
  };

  const validateStep1 = () => {
    if (!form.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Email inválido');
      return false;
    }
    if (form.password.length < 6) {
      setError('Senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError('Senhas não conferem');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.organizationName.trim()) {
      setError('Nome da organização é obrigatório');
      return false;
    }
    if (!form.organizationSlug.trim() || form.organizationSlug.length < 3) {
      setError('Apelido deve ter pelo menos 3 caracteres');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(form.organizationSlug)) {
      setError('Apelido pode conter apenas letras minúsculas, números e hífens');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const prevStep = () => {
    setStep(1);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep2()) return;
    
    setLoading(true);
    setError('');

    try {
      // Chamar API de registro
      const response = await apiService.register({
        nome: form.name,
        email: form.email,
        senha: form.password,
        organizationName: form.organizationName,
        organizationSlug: form.organizationSlug,
        planId: form.planId
      });

      // Salvar token e redirecionar para dashboard
      localStorage.setItem('token', response.token);
      localStorage.setItem('usuario', JSON.stringify(response.user));
      localStorage.setItem('organization', JSON.stringify(response.organization));
      
      navigate('/dashboard');
    } catch (err: any) {
      const message = err.response?.data?.message || err.response?.data?.error || 'Erro ao criar conta. Tente novamente.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === form.planId) || plans[0];

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-left">
          <Link to="/" className="register-brand">
            <Logo size="small" showText={false} logoName="logo2.png" />
          </Link>
          
          <div className="register-benefits">
            <h2>Comece a vender mais hoje</h2>
            <ul>
              <li>
                <span className="benefit-icon">✓</span>
                <span>Configuração em menos de 2 minutos</span>
              </li>
              <li>
                <span className="benefit-icon">✓</span>
                <span>Sem cartão de crédito para começar</span>
              </li>
              <li>
                <span className="benefit-icon">✓</span>
                <span>Suporte em português</span>
              </li>
              <li>
                <span className="benefit-icon">✓</span>
                <span>Cancele quando quiser</span>
              </li>
            </ul>
          </div>

          <div className="register-testimonial">
            <p>"O Ghi Connect mudou a forma como gerenciamos nossos leads. Simples e eficiente!"</p>
            <div className="testimonial-author">
              <span className="author-avatar">RS</span>
              <div>
                <strong>Roberto Silva</strong>
                <span>Diretor Comercial</span>
              </div>
            </div>
          </div>
        </div>

        <div className="register-right">
          <div className="register-form-container">
            <div className="register-header">
              <h1>Criar sua conta</h1>
              <p>
                Já tem uma conta? <Link to="/login">Entrar</Link>
              </p>
            </div>

            <div className="register-steps">
              <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                <span className="step-number">1</span>
                <span className="step-label">Seus dados</span>
              </div>
              <div className="step-line"></div>
              <div className={`step ${step >= 2 ? 'active' : ''}`}>
                <span className="step-number">2</span>
                <span className="step-label">Organização</span>
              </div>
            </div>

            {error && (
              <div className="register-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {step === 1 && (
                <div className="form-step">
                  <div className="form-group">
                    <label htmlFor="name">Nome completo</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Seu nome"
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Senha</label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      placeholder="Mínimo 6 caracteres"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirmar senha</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      placeholder="Repita a senha"
                    />
                  </div>

                  <button type="button" className="btn btn-primary btn-block" onClick={nextStep}>
                    Continuar
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="form-step">
                  <div className="form-group">
                    <label htmlFor="organizationName">Nome da organização</label>
                    <input
                      type="text"
                      id="organizationName"
                      name="organizationName"
                      value={form.organizationName}
                      onChange={handleChange}
                      placeholder="Ex: Minha Empresa"
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="organizationSlug">
                      Apelido (URL)
                      <span className="label-hint">Será usado no endereço do seu CRM</span>
                    </label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">ghiconnect.com/</span>
                      <input
                        type="text"
                        id="organizationSlug"
                        name="organizationSlug"
                        value={form.organizationSlug}
                        onChange={handleChange}
                        placeholder="minha-empresa"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="planId">Plano</label>
                    <select
                      id="planId"
                      name="planId"
                      value={form.planId}
                      onChange={handleChange}
                    >
                      {plans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - {plan.price === 0 ? 'Grátis' : plan.price < 0 ? 'Sob consulta' : `R$${plan.price}/mês`} ({plan.price < 0 ? 'Limites personalizados' : typeof plan.users === 'number' ? `até ${plan.users} usuários` : `${plan.users} usuários`})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="selected-plan-summary">
                    <div className="plan-summary-header">
                      <span>Plano selecionado</span>
                      <strong>{selectedPlan.name}</strong>
                    </div>
                    <div className="plan-summary-price">
                      {selectedPlan.price === 0 ? (
                        <span className="price-free">Grátis</span>
                      ) : (
                        <>
                          <span className="price-amount">R${selectedPlan.price}</span>
                          <span className="price-period">/mês</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn btn-ghost" onClick={prevStep}>
                      Voltar
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? 'Criando conta...' : 'Criar conta'}
                    </button>
                  </div>
                </div>
              )}
            </form>

            <p className="register-terms">
              Ao criar sua conta, você concorda com nossos{' '}
              <a href="#terms">Termos de Uso</a> e{' '}
              <a href="#privacy">Política de Privacidade</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
